<template>
  <div class="features-page">
    <Header />
    <el-main>
      <div class="features-inner dca-container">
        <div class="page-header">
          <h1 class="page-title">功能特性</h1>
          <p class="page-subtitle">基于点云通 {{ guideVersion }} 能力整理的结构化导览</p>
        </div>

        <div class="content-layout">
          <div class="nav-tree-container">
            <NavTree
              :directories="directories"
              :selectedIndex="selectedIndex"
              @select="handleSelect"
            />
          </div>

          <div class="content-display" v-if="currentSection">
            <div class="section-meta">
              <el-tag v-if="currentSection.badge" type="success" effect="dark" size="small">
                {{ currentSection.badge }}
              </el-tag>
              <h2 class="section-title">{{ currentSection.title }}</h2>
            </div>

            <p class="section-summary">{{ currentSection.summary }}</p>

            <ul v-if="currentSection.points?.length" class="section-points">
              <li v-for="point in currentSection.points" :key="point">{{ point }}</li>
            </ul>

            <div v-if="currentSection.image" class="section-media">
              <el-image
                :src="currentSection.image"
                :alt="currentSection.title"
                fit="contain"
                :preview-src-list="[currentSection.image]"
                preview-teleported
                class="section-image"
              />
            </div>
          </div>

          <div v-else class="content-display content-display--empty">
            <p>请选择左侧章节查看功能介绍</p>
          </div>
        </div>
      </div>
    </el-main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import Header from './Header.vue';
import NavTree from './NavTree.vue';
import featureGuide from '@/assets/config/feature_guide.json';
import { useHead } from '@vueuse/head';

useHead({
  title: '功能特性 - DiCloudAccess 点云通',
});

const directories = ref([]);
const selectedIndex = ref(null);
const sectionMap = ref(new Map());

const guideVersion = featureGuide.version || '2.2.10.1';

const currentSection = computed(() => {
  if (selectedIndex.value == null) return null;
  return sectionMap.value.get(selectedIndex.value) || null;
});

const buildSectionMap = (dirs) => {
  const map = new Map();
  dirs.forEach((dir) => {
    (dir.sections || []).forEach((section) => {
      map.set(section.index, section);
    });
  });
  return map;
};

onMounted(() => {
  directories.value = (featureGuide.directories || []).map((dir) => ({
    ...dir,
    isOpen: false,
  }));
  sectionMap.value = buildSectionMap(directories.value);

  if (directories.value.length > 0 && directories.value[0].sections?.length > 0) {
    handleSelect(directories.value[0].sections[0].index);
  }
});

const handleSelect = (index) => {
  selectedIndex.value = index;
};
</script>

<style scoped>
.features-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--dca-bg);
}

.el-main {
  padding: 0;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.features-inner {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 20px;
  padding-top: 24px;
  padding-bottom: 32px;
  min-height: 0;
  width: 100%;
}

.page-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.page-title {
  margin: 0;
  font-size: 28px;
  font-weight: var(--dca-fw-bold);
  color: var(--dca-text);
  letter-spacing: -0.02em;
}

.page-subtitle {
  margin: 0;
  color: var(--dca-text-muted);
  font-size: 14px;
}

.content-layout {
  display: flex;
  flex-direction: row;
  flex-grow: 1;
  min-height: 0;
  overflow: hidden;
  border: 1px solid var(--dca-border);
  border-radius: var(--dca-radius-lg);
  background: var(--dca-surface);
}

.nav-tree-container {
  width: 260px;
  min-width: 260px;
  overflow-y: auto;
  border-right: 1px solid var(--dca-border);
}

.content-display {
  flex-grow: 1;
  padding: 28px 32px;
  overflow-y: auto;
}

.content-display--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--dca-text-muted);
}

.section-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 16px;
}

.section-title {
  margin: 0;
  font-size: 24px;
  font-weight: var(--dca-fw-semibold);
  color: var(--dca-text);
}

.section-summary {
  margin: 0 0 20px;
  color: var(--dca-text-muted);
  line-height: 1.7;
  font-size: 15px;
}

.section-points {
  margin: 0 0 28px;
  padding-left: 20px;
  color: var(--dca-text);
}

.section-points li {
  margin-bottom: 10px;
  line-height: 1.6;
  color: var(--dca-text-muted);
}

.section-media {
  width: 100%;
  border: 1px solid var(--dca-border);
  border-radius: var(--dca-radius);
  background: var(--dca-surface-2);
  overflow: hidden;
}

.section-image {
  display: block;
  width: 100%;
  min-height: 220px;
  max-height: 62vh;
}

.section-image :deep(img) {
  width: 100%;
  max-height: 62vh;
  object-fit: contain;
  background: var(--dca-bg-2);
}

@media (max-width: 768px) {
  .features-inner {
    padding-top: 16px;
    padding-bottom: 20px;
    gap: 14px;
  }

  .page-title {
    font-size: 22px;
  }

  .content-layout {
    flex-direction: column;
  }

  .nav-tree-container {
    width: 100%;
    min-width: unset;
    max-height: 240px;
    border-right: none;
    border-bottom: 1px solid var(--dca-border);
  }

  .content-display {
    padding: 20px 16px;
  }

  .section-title {
    font-size: 20px;
  }

  .section-image,
  .section-image :deep(img) {
    max-height: 42vh;
  }
}
</style>
